import { ToggleButtonGroup, Button, FormHelperText, FormLabel } from "@mui/joy";
import { SxProps } from "@mui/joy/styles/types";
import { useController, useFormContext } from "react-hook-form";

import { useDeepMemo } from "@/hooks/useDeepMemo";

import FormGroup from "./FormGroup";

type Props<V, M extends boolean = false> = {
  options: { name: string; value: V; sx?: SxProps }[];
  value: M extends true ? V[] : V | null;
  onChange: (value: M extends true ? V[] : V | null) => void;
  sx?: SxProps;
  allName?: string;
  nullable?: boolean;
  disabled?: boolean;
};

export const ToggleButtonSelect = <
  // V is convertible to string or null
  V,
  M extends boolean = false,
>({
  options,
  value,
  onChange,
  sx,
  allName,
  nullable,
  disabled,
}: Props<V, M>) => {
  const isAllSelected = allName && Array.isArray(value) && value.length === options.length;

  // Whether several options can be selected depends on whether value is an
  // array at runtime, which TypeScript can't link to the type of onChange.
  const emitChange = onChange as (value: V[] | V | null) => void;

  const optionIdsToValues = useDeepMemo(
    (opts: typeof options) =>
      Object.fromEntries(opts.map((o) => [JSON.stringify(o.value), o.value])),
    options,
  );

  const valueAsJson = Array.isArray(value)
    ? isAllSelected
      ? ["$ALL"]
      : value.map((v) => JSON.stringify(v))
    : JSON.stringify(value);

  console.log({ value });

  return (
    <ToggleButtonGroup
      disabled={disabled}
      value={valueAsJson}
      // @ts-expect-error - null handling
      onChange={(e, v) => {
        // onChange(options.find((o) => String(o.value) === v)?.value ?? null)
        if (Array.isArray(v)) {
          if (
            v.includes("$ALL") && // v != ['$ALL']
            valueAsJson.length !== 1 &&
            valueAsJson[0] !== "$ALL"
          ) {
            emitChange(Object.values(optionIdsToValues));
          } else {
            emitChange(v.filter((id) => id !== "$ALL").map((id) => optionIdsToValues[id]));
          }
        } else {
          if (v === null) {
            if (nullable) {
              emitChange(null);
            }
          } else {
            emitChange(optionIdsToValues[v]);
          }
        }
      }}
      spacing={0.75}
      variant="soft"
      sx={{
        flexFlow: "wrap",
        paddingX: 0,
        ".MuiButton-root": {
          paddingX: 1.25,
          // @ts-expect-error - unknown prop
          ...(sx && sx?.[".MuiButton-root"]),
        },
        ...sx,
      }}
    >
      {allName && (
        <Button
          value="$ALL"
          color={isAllSelected ? "primary" : "neutral"}
          variant={isAllSelected ? "solid" : "soft"}
        >
          {allName}
        </Button>
      )}
      {options.map((option, _i) => {
        const optionId = JSON.stringify(option.value);
        const isSelected =
          !isAllSelected && value instanceof Array
            ? valueAsJson.includes(optionId)
            : valueAsJson === optionId;
        return (
          <Button
            key={optionId}
            value={optionId}
            color={isSelected ? "primary" : "neutral"}
            variant={isSelected ? "solid" : "soft"}
            sx={option.sx}
          >
            {option.name}
          </Button>
        );
      })}
    </ToggleButtonGroup>
  );
};

export const ToggleButtonSelectInput = <V,>({
  name,
  label,
  options,
  allName,
  sx,
  nullable = false,
  disabled,
}: {
  name: string;
  label: string | null;
  options: { name: string; value: V }[];
  allName?: string;
  sx?: SxProps;
  nullable?: boolean;
  disabled?: boolean;
}) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
  });

  return (
    <FormGroup error={Boolean(fieldState.error)}>
      {label ? <FormLabel>{label}</FormLabel> : null}
      <ToggleButtonSelect
        options={options}
        value={field.value}
        allName={allName}
        onChange={field.onChange}
        sx={sx}
        nullable={nullable}
        disabled={disabled}
      />
      <FormHelperText>{fieldState.error?.message}</FormHelperText>
    </FormGroup>
  );
};
